import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { createContext, useContext } from 'react';
import type React from 'react';

const VerticalContext = createContext(false);

const NoneSelectedContext = createContext(false);

type TabsProps = { vertical?: boolean; className?: string; selectedIndex?: number | null } & Omit<
  React.ComponentPropsWithoutRef<typeof Headless.TabGroup>,
  'className' | 'selectedIndex'
>;

export function Tabs({ vertical, className, selectedIndex, ...props }: TabsProps) {
  const noneSelected = selectedIndex === null || selectedIndex === -1;
  return (
    <VerticalContext.Provider value={vertical ?? false}>
      <NoneSelectedContext.Provider value={noneSelected}>
        <Headless.TabGroup
          vertical={vertical}
          selectedIndex={noneSelected ? -1 : selectedIndex}
          {...props}
          className={clsx(vertical ? 'flex flex-row' : 'flex flex-col', className)}
        />
      </NoneSelectedContext.Provider>
    </VerticalContext.Provider>
  );
}

export function TabList({
  className,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<typeof Headless.TabList>,
  'className'
>) {
  const vertical = useContext(VerticalContext);
  return (
    <Headless.TabList
      {...props}
      className={clsx(
        'text-sm/6',
        vertical
          ? 'flex flex-col gap-0.5 border-r border-border pr-2'
          : 'flex gap-4 border-b border-border',
        className
      )}
    />
  );
}

export function Tab({
  className,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<typeof Headless.Tab>,
  'className'
>) {
  const vertical = useContext(VerticalContext);
  const noneSelected = useContext(NoneSelectedContext);
  return (
    <Headless.Tab
      {...props}
      className={clsx(
        'cursor-pointer transition-colors outline-none',
        vertical
          ? [
              'h-8 flex items-center rounded-sm px-2 text-left text-sm',
              'text-foreground/80 data-hover:text-foreground data-hover:bg-muted-foreground/10',
              !noneSelected && 'data-selected:bg-muted-foreground/15 data-selected:text-foreground',
            ]
          : [
              'text-muted-foreground data-hover:text-foreground',
              !noneSelected && 'data-selected:text-foreground data-selected:font-medium',
              '-mb-px border-b-2 border-transparent pb-2.5 pt-2',
              !noneSelected && 'data-selected:border-primary',
            ],
        className
      )}
    />
  );
}

export function TabPanels({
  className,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<typeof Headless.TabPanels>,
  'className'
>) {
  const vertical = useContext(VerticalContext);
  return (
    <Headless.TabPanels {...props} className={clsx(vertical ? 'flex-1 pl-4' : 'mt-4', className)} />
  );
}

export function TabPanel({
  className,
  ...props
}: { className?: string } & Omit<
  React.ComponentPropsWithoutRef<typeof Headless.TabPanel>,
  'className'
>) {
  return <Headless.TabPanel {...props} className={clsx('focus:outline-none', className)} />;
}
